import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Injector,
    OnDestroy,
    OnInit,
    QueryList,
    Type,
    ViewChild,
    ViewChildren
} from "@angular/core";
import {CanvasBranch, CanvasItem, CanvasNode, CanvasWorkflow, Connection} from "./canvas-workflow.interface";
import {ActivatedRoute, Router} from "@angular/router";
import {ConfirmationService, MenuItem, MessageService} from "primeng/api";
import {TranslateService} from "@ngx-translate/core";
import {PositionCalculationService} from "./position-calculation.service";
import {PanZoomService} from "./pan-zoom.service";
import {ConnectionService} from "./connection.service";
import {WorkflowService} from "./canvas-workflow.service";
import {CdkDrag, CdkDragDrop, CdkDragEnter, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {StepConfigRegistryService} from "./step-config-registry.service";
import {
    AbstractStepDTO,
    ConfiguratorScenarioService,
    EngineScenarioService,
    ScenarioDTO,
    StepCountProjection
} from "@tj/tj-dmp-lib-api";
import {
    ON_CANCEL_TOKEN,
    ON_SAVE_TOKEN,
    PARTNER_TOKEN,
    READ_ONLY_MODE_TOKEN,
    STEP_TOKEN,
    TENANT_TOKEN,
    ENTRY_STEP_TOKEN
} from "../../../components/workflow-step-config/injection-tokens";
import {takeUntil} from "rxjs/operators";
import {StepDescriptionLookups, StepDescriptionService} from "../../../components/workflow-step-config/step-description.service";
import {Subject} from "rxjs";
import {UndoRedoService} from "./undo-redo.service";

@Component({
    selector: "dmp-workflow-board",
    templateUrl: "./workflow-board.component.html",
    styleUrls: ["./workflow-board.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class WorkflowBoardComponent implements OnInit, OnDestroy {
    @ViewChild("board", { static: true }) board!: ElementRef;
    @ViewChild('canvasViewport', { static: false }) canvasViewportRef!: ElementRef;
    @ViewChildren('canvasNode', { read: ElementRef }) canvasNodeRefs!: QueryList<ElementRef<HTMLElement>>;

    /** Hauteur mesurée de chaque bloc, par id de nœud : elle suit le contenu du bloc, et le placement
     *  des branches et des flèches en dépend. */
    private readonly nodeHeights = new Map<string, number>();
    private nodeResizeObserver?: ResizeObserver;

    private destroy$ = new Subject<void>();

    tenant: string;
    stepDescriptionLookups: StepDescriptionLookups;
    partner: "AV";
    id: string;
    version: number;

    current: ScenarioDTO;

    searchItem: string;
    toggledSidebar = false;

    // Step counts from API
    stepCounts: Map<string, number> = new Map();
    selectedStatus: string | null = null;
    private isReloading = false;

    // Process status enum for filtering
    ProcessStatusEnum = {
        RUNNING: 'RUNNING',
        COMPLETED: 'COMPLETED',
        FAILED: 'FAILED',
        STOPPED: 'STOPPED',
        PAUSED: 'PAUSED'
    };

    statusFilterOptions = [
        { label: 'workflow.board.statusFilter.all', value: null },
        { label: 'workflow.board.statusFilter.running', value: this.ProcessStatusEnum.RUNNING },
        { label: 'workflow.board.statusFilter.completed', value: this.ProcessStatusEnum.COMPLETED },
        { label: 'workflow.board.statusFilter.failed', value: this.ProcessStatusEnum.FAILED },
        { label: 'workflow.board.statusFilter.stopped', value: this.ProcessStatusEnum.STOPPED },
        { label: 'workflow.board.statusFilter.paused', value: this.ProcessStatusEnum.PAUSED }
    ];

    ruleItems: CanvasItem[] = [{
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.LANDINGPAGEEVENT}`,
        type: AbstractStepDTO.TypeEnum.LANDINGPAGEEVENT,
    }, {
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.CHANGEDATAEVENT}`,
        type: AbstractStepDTO.TypeEnum.CHANGEDATAEVENT,
    }, {
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.EMAILEVENT}`,
        type: AbstractStepDTO.TypeEnum.EMAILEVENT,
    }, {
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.DELAY}`,
        type: AbstractStepDTO.TypeEnum.DELAY
    }, {
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.CONDITIONIF}`,
        type: AbstractStepDTO.TypeEnum.CONDITIONIF
    }];
    actionItems: CanvasItem[] = [{
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.EMAIL}`,
        type: AbstractStepDTO.TypeEnum.EMAIL,
    }, {
        name: `workflow.board.sourceItem.${AbstractStepDTO.TypeEnum.SMS}`,
        type: AbstractStepDTO.TypeEnum.SMS,
    }];
    sourceItems: CanvasItem[] = [...this.actionItems, ...this.ruleItems];
    private readonly systemActionTypes: AbstractStepDTO.TypeEnum[] = [
        AbstractStepDTO.TypeEnum.SAVEQUERY
    ];

    canvasWorkflow!: CanvasWorkflow;
    draggedNode: string | null = null;
    isDragging = false;
    connections: Connection[] = [];
    connectedDropIds: string[] = [];
    startNodeId: string;

    // Menu d'actions
    nodeMenuItems: MenuItem[] = [];
    currentSelectedNode: CanvasNode | null = null;
    currentSelectedBranch: CanvasBranch | null = null;

    // Panel de modification
    showEditPanel = false;
    private readonly bodyDrawerClass = 'workflow-scenario-drawer-open';

    isEditingMode = false;

    private readonly CANVAS_WIDTH = 1200;
    private readonly CANVAS_HEIGHT = 800
    private viewportSize = { width: this.CANVAS_WIDTH, height: this.CANVAS_HEIGHT };
    private _canvasSize = { width: this.CANVAS_WIDTH, height: this.CANVAS_HEIGHT };

    constructor(
        private confirmationService: ConfirmationService,
        private route: ActivatedRoute,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private positionService: PositionCalculationService,
        private panZoomService: PanZoomService,
        private connectionService: ConnectionService,
        private workflowService: WorkflowService,
        private injector: Injector,
        private stepConfigRegistry: StepConfigRegistryService,
        private scenarioService: ConfiguratorScenarioService,
        private engineScenarioService: EngineScenarioService,
        private messageService: MessageService,
        private undoRedoService: UndoRedoService,
        private translateService: TranslateService,
        private stepDescriptionService: StepDescriptionService
    ) { }

    @HostListener('window:resize', ['$event'])
    onWindowResize(event: Event) {
        this.updateViewportSize();
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        if (!this.isEditingMode) return;

        // Ctrl+Z pour Undo
        if (event.ctrlKey && event.key === 'z' && !event.shiftKey) {
            event.preventDefault();
            this.undo();
        }

        // Ctrl+Y ou Ctrl+Shift+Z pour Redo
        if (event.ctrlKey && (event.key === 'y' || (event.key === 'z' && event.shiftKey))) {
            event.preventDefault();
            this.redo();
        }
    }

    get actionTypes(): AbstractStepDTO.TypeEnum[] {
        return [
            ...this.actionItems.map(item => item.type),
            ...this.systemActionTypes
        ];
    }

    get ruleTypes(){
        return this.ruleItems.map(item => item.type);
    }

    isActionItem(item: CanvasItem): boolean {
        return this.actionTypes.includes(item.type);
    }

    isRuleItem(item: CanvasItem): boolean {
        return this.ruleTypes.includes(item.type);
    }

    isActionNode(node: CanvasNode): boolean {
        return this.actionTypes.includes(node.step.type);
    }

    isRuleNode(node: CanvasNode): boolean {
        return this.ruleTypes.includes(node.step.type);
    }

    getFilteredItems(): CanvasItem[] {
        if (!this.searchItem) {
            return this.sourceItems;
        }
        return this.sourceItems.filter((item) =>
            item.name.toLowerCase().includes(this.searchItem.toLowerCase())
        );
    }

    ngOnInit(): void {
        this.route.paramMap.pipe(
            takeUntil(this.destroy$)
        ).subscribe(paramMap => {
            this.tenant = paramMap.get('tenant');
            this.id = paramMap.get('id');
            const action = paramMap.get('action');
            this.isEditingMode = action === 'edit';

            this.onParamsChange()
        });

        this.route.queryParamMap.pipe(
            takeUntil(this.destroy$)
        ).subscribe(queryParamMap => {
            const version = parseInt(queryParamMap.get('version'));
            this.version = isNaN(version)? 0 : version;

            this.onParamsChange();
        });
    }

    ngOnDestroy(): void {
       this.nodeResizeObserver?.disconnect();
       document.body.classList.remove(this.bodyDrawerClass);
       this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewInit() {
        this.updateViewportSize();
        this.cdr.detectChanges();

        this.nodeResizeObserver = new ResizeObserver(entries => this.onNodesResized(entries));
        this.observeCanvasNodes();
        this.canvasNodeRefs.changes.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => this.observeCanvasNodes());
    }

    editWorkflow() {
        // Logic to edit the workflow
        console.log("Workflow edited");
    }

    saveWorkflow() {
        if (!this.canvasWorkflow?.rootBranch) {
            this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                detail: this.translateService.instant('workflow.board.messages.missingSteps')
            });
            return;
        }

        const stepDTOs = this.workflowService.createStepsFromBranch(this.canvasWorkflow.rootBranch)

        this.scenarioService.updateSteps(stepDTOs, this.tenant, this.id, this.version).subscribe({
            next: (data) => {
                this.messageService.add({ severity: 'success', summary: this.translateService.instant('workflow.board.messages.successSummary'), detail: this.translateService.instant('workflow.board.messages.success') });
                if (data?.version && data.version !== this.version) {
                    this.version = data.version;
                    this.router.navigate([], {
                        relativeTo: this.route,
                        queryParams: { version: data.version },
                        queryParamsHandling: 'merge'
                    });
                }
                this.reloadScenarioData();
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error', summary: this.translateService.instant('workflow.board.messages.errorSummary'), detail: this.translateService.instant('workflow.board.messages.error', { error: e.toString() })
                })
            }
        })
    }

    testWorkflow() {
        this.engineScenarioService.runTestScenarioById(this.tenant, this.id, this.version, this.partner).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant('workflow.board.messages.successSummary'),
                    detail: this.translateService.instant('workflow.board.messages.testSent')
                });
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                    detail: this.translateService.instant('workflow.board.messages.testError', { error: e.toString() })
                });
            }
        });
    }

    launchWorkflow() {
        // Logic to launch the workflow
        this.scenarioService.updateStatus(this.tenant, this.partner, this.id, this.version, ScenarioDTO.StatusScenarioEnum.INPROGRESS).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant('workflow.board.messages.successSummary'),
                    detail: this.translateService.instant('workflow.board.messages.launched')
                });
                // Reload scenario data to update the UI with new status
                this.onParamsChange();
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                    detail: this.translateService.instant('workflow.board.messages.launchError', { error: e.toString() })
                });
            }
        });
    }

    pauseWorkflow() {
        // Logic to pause the workflow
        this.scenarioService.updateStatus(this.tenant, this.partner, this.id, this.version, ScenarioDTO.StatusScenarioEnum.ONPAUSE).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant('workflow.board.messages.successSummary'),
                    detail: this.translateService.instant('workflow.board.messages.paused')
                });
                // Reload scenario data to update the UI with new status
                this.onParamsChange();
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                    detail: this.translateService.instant('workflow.board.messages.pauseError', { error: e.toString() })
                });
            }
        });
    }

    stopWorkflow() {
        // Logic to stop the workflow
        this.scenarioService.updateStatus(this.tenant, this.partner, this.id, this.version, ScenarioDTO.StatusScenarioEnum.STOPPED).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: this.translateService.instant('workflow.board.messages.successSummary'),
                    detail: this.translateService.instant('workflow.board.messages.stopped')
                });
                // Reload scenario data to update the UI with new status
                this.onParamsChange();
            },
            error: (e) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                    detail: this.translateService.instant('workflow.board.messages.stopError', { error: e.toString() })
                });
            }
        });
    }

    leaveEditor() {
        this.confirmationService.confirm({
            message: this.translateService.instant("workflow.confirmation.leave-board"),
            accept: () => {
                this.router.navigate(
                    ['/h/workflow', this.tenant, this.id, this.isEditingMode ? "edit" : "view"],
                    {
                        queryParams: {
                            version: this.version
                        }
                    }
                );
            },
        });
    }

    showSidebar() {
        this.toggledSidebar = !this.toggledSidebar;
    }

    switchToEditMode() {
        this.router.navigate(
            ['/h/workflow/board', this.tenant, this.id, "edit"],
            {
                queryParams: {
                    version: this.version
                }
            }
        );
    }

    private switchToReadOnlyMode() {
        this.router.navigate(
            ['/h/workflow/board', this.tenant, this.id, "view"],
            {
                queryParams: {
                    version: this.version
                }
            }
        );
    }

    private onParamsChange() {
        if (this.tenant && this.id && this.version > 0) {
            this.scenarioService.getVersion(this.tenant, this.id, this.version).subscribe(scenario => {
                this.current = scenario;
                this.partner = scenario.affiliateCode as "AV";

                if(this.current.statusScenario === 'ARCHIVE' && this.isEditingMode){
                    this.switchToReadOnlyMode();
                }
                if (!this.initializeCanvasFromScenario(this.current)) {
                    return;
                }
                this.undoRedoService.clear();
                this.saveCurrentState(this.translateService.instant('workflow.board.actions.initialState'));
                this.loadStepDescriptionLookups();

                // Load step counts
                this.loadStepCounts();
            });
        }
    }

    /** Description de l'étape dans la langue courante ; repli sur la description enregistrée. */
    stepDescription(step: AbstractStepDTO): string {
        if (this.stepDescriptionLookups && step) {
            const description = this.stepDescriptionService.describe(step, this.stepDescriptionLookups);
            if (description) {
                return description;
            }
        }
        return step?.description;
    }

    private loadStepDescriptionLookups(): void {
        const steps: AbstractStepDTO[] = [];
        const collect = (list: AbstractStepDTO[]) => (list ?? []).forEach(step => {
            steps.push(step);
            collect((step as any).caseTrue);
            collect((step as any).caseFalse);
        });
        collect(this.current?.steps as AbstractStepDTO[]);
        this.stepDescriptionService.loadLookups(this.tenant, this.partner, steps).subscribe(lookups => {
            this.stepDescriptionLookups = lookups;
            this.cdr.detectChanges();
        });
    }

    /**
     * Load step counts from the engine API
     */
    private loadStepCounts(): void {
        if (!this.tenant || !this.id || !this.version) {
            return;
        }

        this.engineScenarioService.getStepsCount(
            this.tenant,
            this.id,
            this.version,
            this.selectedStatus || undefined
        ).subscribe({
            next: (counts: StepCountProjection[]) => {
                this.stepCounts.clear();
                counts.forEach(count => {
                    this.stepCounts.set(count.stepPath, count.contactCount);
                });
                this.cdr.detectChanges();
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'warn',
                    summary: this.translateService.instant('workflow.board.messages.warnSummary'),
                    detail: this.translateService.instant('workflow.board.messages.stepCountsError')
                });
            }
        });
    }

    /**
     * Get the contact count for a specific step path
     */
    getStepCount(stepPath: string | undefined): number {
        if (!stepPath) {
            return 0;
        }
        return this.stepCounts.get(stepPath) || 0;
    }

    /**
     * Check if a step is unsaved (doesn't have a stepPath yet)
     */
    isStepUnsaved(stepPath: string | undefined): boolean {
        return !stepPath || stepPath === '';
    }

    /**
     * Reload step counts (can be called when status filter changes)
     */
    reloadStepCounts(): void {
        this.loadStepCounts();
    }

    /**
     * Handle status filter change
     */
    onStatusFilterChange(): void {
        this.reloadStepCounts();
    }

    /**
     * Get the label for a status value
     */
    getStatusLabel(status: string | null): string {
        if (!status) {
            return 'workflow.board.statusFilter.all';
        }
        const option = this.statusFilterOptions.find(opt => opt.value === status);
        return option ? option.label : 'workflow.board.statusFilter.all';
    }

    /**
     * Reload the entire scenario data (steps + counts) after save
     */
    private reloadScenarioData(): void {
        if (this.isReloading) {
            return;
        }

        this.isReloading = true;

        this.scenarioService.getVersion(this.tenant, this.id, this.version).subscribe({
            next: (scenario) => {
                this.current = scenario;
                if (this.initializeCanvasFromScenario(scenario)) {
                    this.reloadStepCounts();
                    this.loadStepDescriptionLookups();
                }
                this.cdr.detectChanges();
                this.isReloading = false;
            },
            error: (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                    detail: this.translateService.instant('workflow.board.messages.reloadError')
                });
                this.isReloading = false;
            }
        });
    }

    // ====================================================================
    // UNDO / REDO
    // ====================================================================

    undo(): void {
        const previousState = this.undoRedoService.undo();
        if (previousState) {
            this.canvasWorkflow = previousState;
            this.updateWorkflow();
        }
    }

    redo(): void {
        const nextState = this.undoRedoService.redo();
        if (nextState) {
            this.canvasWorkflow = nextState;
            this.updateWorkflow();
        }
    }

    get canUndo(): boolean {
        return this.undoRedoService.canUndo();
    }

    get canRedo(): boolean {
        return this.undoRedoService.canRedo();
    }

    get undoActionLabel(): string {
        const action = this.undoRedoService.getUndoAction();
        return action
            ? this.translateService.instant('workflow.board.undo.undoAction', { action: action })
            : this.translateService.instant('workflow.board.undo.undo');
    }

    get redoActionLabel(): string {
        const action = this.undoRedoService.getRedoAction();
        return action
            ? this.translateService.instant('workflow.board.undo.redoAction', { action: action })
            : this.translateService.instant('workflow.board.undo.redo');
    }


    private saveCurrentState(actionDescription: string): void {
        if (this.isEditingMode && this.canvasWorkflow) {
            this.undoRedoService.saveState(this.canvasWorkflow, actionDescription);
        }
    }

    // ====================================================================
    // GETTERS UTILITAIRES
    // ====================================================================

    get mainBranch(): CanvasBranch {
        return this.canvasWorkflow.rootBranch;
    }

    get allBranches(): CanvasBranch[] {
        return this.workflowService.getAllBranches(this.canvasWorkflow.rootBranch);
    }

    get allNodes(): CanvasNode[] {
        return this.workflowService.getAllNodes(this.canvasWorkflow.rootBranch);
    }

    get canvasSize(): { width: number, height: number } {
        return this._canvasSize;
    }

    get shouldShowSidebar(): boolean {
        if(this.isEditingMode){
            return this.toggledSidebar;
        }
        return true;
    }

    get shouldShowEditPanel(): boolean {
        return this.isEditingMode && this.showEditPanel;
    }

    get shouldShowConnections(): boolean {
        return !this.isDragging;
    }

    // Délégation des propriétés du service PanZoom
    get zoomLevel(): number { return this.panZoomService.zoomLevel; }
    get minZoom(): number { return this.panZoomService.minZoom; }
    get maxZoom(): number { return this.panZoomService.maxZoom; }
    get zoomPercentage(): number { return this.panZoomService.zoomPercentage; }
    get canvasTransform(): string { return this.panZoomService.getCanvasTransform(); }
    get isPanning(): boolean { return this.panZoomService.isPanning; }

    // Délégation des constantes du service Position
    get COMPONENT_WIDTH(): number { return this.positionService.COMPONENT_WIDTH; }
    get NODE_SPACING(): number { return this.positionService.NODE_SPACING; }
    get BRANCH_PADDING(): number { return this.positionService.BRANCH_PADDING; }

    // ====================================================================
    // MISE À JOUR ET CALCULS
    // ====================================================================

    private updateViewportSize() {
        if (this.canvasViewportRef?.nativeElement) {
            const element = this.canvasViewportRef.nativeElement;
            this.viewportSize = {
                width: element.clientWidth,
                height: element.clientHeight
            };
            this.updateCanvasSize();
            this.cdr.detectChanges();
        }
    }

    private updateWorkflow(): void {
        this.layoutBranches();
        this.connectedDropIds = this.allBranches.filter(b => b.type!= 'start').map(b => b.id);
        this.centerWorkflow();
    }

    private layoutBranches(): void {
        this.positionService.updateBranchesBounds(this.canvasWorkflow.rootBranch, this.nodeHeights);
        this.connections = this.connectionService.generateConnections(this.canvasWorkflow.rootBranch);
        this.updateCanvasSize();
    }

    private observeCanvasNodes(): void {
        this.nodeResizeObserver.disconnect();
        this.canvasNodeRefs.forEach(ref => this.nodeResizeObserver.observe(ref.nativeElement));
    }

    /**
     * Replace les branches et les flèches quand la hauteur d'un bloc change (premier rendu, description
     * modifiée…). Ignoré pendant un glisser-déposer : CDK sort alors le bloc déplacé du flux, et le dépôt
     * refait le placement.
     */
    private onNodesResized(entries: ResizeObserverEntry[]): void {
        if (this.isDragging || !this.canvasWorkflow) return;

        const firstMeasure = this.nodeHeights.size === 0;
        let changed = false;
        for (const entry of entries) {
            const element = entry.target as HTMLElement;
            const nodeId = element.dataset.nodeId;
            const height = element.offsetHeight;
            if (!nodeId || !height || this.nodeHeights.get(nodeId) === height) continue;
            this.nodeHeights.set(nodeId, height);
            changed = true;
        }
        if (!changed) return;

        this.layoutBranches();
        // Au chargement, le centrage a été calculé sur des hauteurs estimées.
        if (firstMeasure) this.centerWorkflow();
        this.cdr.detectChanges();
    }

    private updateCanvasSize() {
        if(this.canvasWorkflow){
            this._canvasSize = this.positionService.calculateCanvasSize(this.allBranches, this.viewportSize, this.zoomLevel);
        }
        this.panZoomService.clampPanOffset(this._canvasSize, this.viewportSize);
    }
    // ====================================================================
    // DRAG & DROP
    // ====================================================================

    getAllDropIds(): string[] {
        return this.connectedDropIds;
    }

    drop(event: CdkDragDrop<any>, branch: CanvasBranch): void {
        if (!this.isEditingMode) return;
        this.isDragging = false;

        const canInsert = this.getSortPredicate(branch)(event.currentIndex, event.item);
        if (!canInsert) {
            this.messageService.add({ severity: 'warn', summary: this.translateService.instant('workflow.board.messages.warnSummary'), detail: this.translateService.instant('workflow.board.messages.warn') });
            this.updateWorkflow();
            return;
        }
        let newNode: CanvasNode | null = null;
        if (event.previousContainer !== event.container && event.previousContainer.id === 'sourceList') {
            const draggedItem = event.item.data as CanvasItem;
            newNode = this.workflowService.addItemToBranch(draggedItem, branch, event.currentIndex, this.canvasWorkflow);
        } else {
            if(event.previousContainer.id === branch.id){
                moveItemInArray(branch.nodes, event.previousIndex, event.currentIndex);
                newNode = branch.nodes[event.currentIndex];
            } else {
                transferArrayItem(
                    event.previousContainer.data,
                    branch.nodes,
                    event.previousIndex,
                    event.currentIndex
                );
                newNode = branch.nodes[event.currentIndex];
            }
            newNode.step.updatedAt = new Date();
        }

        this.updateWorkflow();
        this.saveCurrentState(this.translateService.instant('workflow.board.actions.addElement'));

        if (newNode) {
            this.currentSelectedNode = newNode;
            this.currentSelectedBranch = branch;
        }
    }

    isDragDisabled(node: CanvasNode): boolean {
        if (!this.isEditingMode) return true;
        return this.workflowService.isDragDisabled(node);
    }

    getSortPredicate(branch: CanvasBranch) {
        return (index: number, item: CdkDrag<any>) => {

            if(item.data.branch?.id === branch.id && index === branch.nodes.length-1){
                return false;
            }
            if(item.data.branch?.id !== branch.id && index === branch.nodes.length){
                return false;
            }
           return true;
        };
    }

    onDragEntered(event: CdkDragEnter<any>, branch: CanvasBranch) {
        const index = branch.nodes.length;
        const allowed = this.getSortPredicate(branch)(index, event.item);

        if (!allowed) {
            event.item._dragRef.reset();
        }
    }
    // ====================================================================
    // ÉVÉNEMENTS DRAG
    // ====================================================================

    onDragStarted() {
        if (!this.isEditingMode) return true;
        this.isDragging = true;
    }

    onDragEnded() {
        if (!this.isEditingMode) return true;
        this.isDragging = false;
    }

    // ====================================================================
    // MÉTHODES ZOOM - DÉLÉGATION AU SERVICE
    // ====================================================================

    zoomIn() {
        this.panZoomService.zoomIn();
        this.updateCanvasSize()
    }

    zoomOut() {
        this.panZoomService.zoomOut();
        this.updateCanvasSize()
    }

    resetZoom() {
        this.panZoomService.resetZoom();
        this.updateCanvasSize()
    }

    fitToScreen() {
        this.panZoomService.fitToScreen(this.allBranches, this.viewportSize.width, this.viewportSize.height);
        this.updateCanvasSize()
    }

    centerWorkflow() {
        this.panZoomService.centerWorkflow(this.allBranches, this.viewportSize.width, this.viewportSize.height);
    }

    onMouseWheel(event: WheelEvent) {
        this.panZoomService.handleMouseWheel(event, event.target as HTMLElement);
        this.updateCanvasSize()
    }

    onMouseDown(event: MouseEvent) {
        const target = event.target as HTMLElement;
        const draggableElement = this.isEditingMode ? target.closest('[cdkDrag], .remove-btn'): null;
        this.panZoomService.startPanning(event, !!draggableElement);
    }

    onMouseMove(event: MouseEvent) {
        this.panZoomService.updatePanning(event, this.canvasSize, this.viewportSize);
    }

    onMouseUp(event: MouseEvent) {
        this.panZoomService.stopPanning();
    }

    onContextMenu(event: MouseEvent) {
        event.preventDefault();
    }

    get canvasContainerStyle() {
        return this.panZoomService.getCanvasContainerStyle(
            this.canvasSize,
            window.innerWidth || 800,
            window.innerHeight || 600
        );
    }

    // ====================================================================
    // MÉTHODES POUR LES CONNEXIONS - DÉLÉGATION AU SERVICE
    // ====================================================================

    getConnectionPath(connection: Connection): string {
        return this.connectionService.getConnectionPath(connection);
    }

    getArrowPath(connection: Connection): string {
        return this.connectionService.getArrowPath(connection);
    }

    trackConnection = (index: number, connection: Connection): string => {
        return connection.id;
    }

    // ====================================================================
    // MENU D'ACTIONS
    // ====================================================================

    /**
     * Méthode appelée à l'ouverture du menu
     */
    onMenuOpen(nodeId: string): void {
        this.closeEditPanel();
        this.allBranches.forEach(branch => {
            branch.nodes.forEach(node => {
                if (node.id === nodeId) {
                    this.currentSelectedNode = node;
                    this.currentSelectedBranch = branch;
                }
            });
        });

        this.buildNodeMenuForCurrentNode();
    }

    /**
     * Construit le menu en fonction du node actuellement sélectionné
     */
    private buildNodeMenuForCurrentNode(): void {
        if (!this.currentSelectedNode) return;

        if (this.isEditingMode) {
            this.nodeMenuItems = [
                {
                    label: this.translateService.instant('workflow.board.menu.edit'),
                    icon: 'fas fa-pen',
                    command: () => this.editNode()
                }
            ];

            // Ajouter "Supprimer" seulement si ce n'est pas le node de départ
            if (!this.currentSelectedNode.isStartNode) {
                this.nodeMenuItems.push({
                    label: this.translateService.instant('workflow.board.menu.delete'),
                    icon: 'far fa-trash-alt',
                    command: () => this.deleteNode(),
                    styleClass: 'menu-item-danger'
                });
            }
        } else {
            this.nodeMenuItems = [
                {
                    label: this.translateService.instant('workflow.board.menu.details'),
                    icon: 'fa far fa-eye',
                    command: () => this.viewNode()
                }
            ];
        }
    }


    /**
    * Méthode pour modifier un node (vide pour le moment)
    */
    editNode(): void {
        if (!this.isEditingMode) return;

        if (this.currentSelectedNode && this.currentSelectedNode.step) {
            this.showEditPanel = true;
            document.body.classList.add(this.bodyDrawerClass);
        }
    }

    viewNode(): void {
        if(this.isEditingMode) return;

        if (this.currentSelectedNode && this.currentSelectedNode.step) {
            this.showEditPanel = true;
            document.body.classList.add(this.bodyDrawerClass);
        }
    }

    /**
     * Méthode pour supprimer un node
     */
    deleteNode(): void {
        if (!this.isEditingMode) return;

        if (this.currentSelectedNode && this.currentSelectedBranch) {
            this.workflowService.deleteNode(this.currentSelectedNode, this.currentSelectedBranch);
            const name = this.translateService.instant(this.currentSelectedNode.name);
            this.saveCurrentState(this.translateService.instant('workflow.board.actions.deleteElement', {name: name}));
            this.closeEditPanel()
            this.updateWorkflow();

            this.currentSelectedNode = null;
            this.currentSelectedBranch = null;
        }
    }

    // ====================================================================
    // PANEL DE MODIFICATION
    // ====================================================================

    get currentStepConfigComponent(): Type<any> | null {
        if (!this.currentSelectedNode?.step) return null;
        return this.stepConfigRegistry.getComponentForStep(this.currentSelectedNode.step.type);
    }

    private _stepConfigInjector: Injector | null = null;
    private _lastSelectedNodeId: string | null = null;

    get stepConfigInjector(): Injector {
        if (!this.currentSelectedNode) return this.injector;

        // Recréer seulement si le node a changé
        if (this._lastSelectedNodeId !== this.currentSelectedNode.id) {
            this._stepConfigInjector = Injector.create({
                providers: [
                    { provide: STEP_TOKEN, useValue: this.currentSelectedNode.step },
                    { provide: ENTRY_STEP_TOKEN, useValue: (this.current?.steps?.[0] as AbstractStepDTO) ?? null },
                    { provide: TENANT_TOKEN, useValue: this.tenant },
                    { provide: PARTNER_TOKEN, useValue: this.partner },
                    { provide: READ_ONLY_MODE_TOKEN, useValue: !this.isEditingMode },
                    { provide: ON_SAVE_TOKEN, useValue: () => this.onStepConfigSaved() },
                    { provide: ON_CANCEL_TOKEN, useValue: () => this.onStepConfigCancelled() }
                ],
                parent: this.injector
            });
            this._lastSelectedNodeId = this.currentSelectedNode.id;
        }

        return this._stepConfigInjector || this.injector;
    }

    private onStepConfigSaved(): void {
        const name = this.translateService.instant(this.currentSelectedNode?.name)
        this.saveCurrentState(this.translateService.instant('workflow.board.actions.editElement', { name: name}));
        this.closeEditPanel();
    }

    private onStepConfigCancelled(): void {
        this.closeEditPanel();
    }

    /** Étape affichée dans le panneau latéral : elle est mise en valeur dans le canevas. */
    isOpenInPanel(node: CanvasNode): boolean {
        return this.showEditPanel && this.currentSelectedNode?.id === node.id;
    }

    /**
     * Ferme le panel de modification
     */
    closeEditPanel(): void {
        this.showEditPanel = false;
        document.body.classList.remove(this.bodyDrawerClass);
        this.currentSelectedNode = null;
        this.currentSelectedBranch = null;
        this.resetStepConfigInjector();
    }

    /**
     * Retourne le titre du panel en fonction du type d'item
     */
    getPanelTitle(): string {
        if (!this.currentSelectedNode?.step) return '';

        const type = this.currentSelectedNode.step.type;
        const name = this.translateService.instant(this.currentSelectedNode.name);

        if (this.ruleTypes.includes(type)) {
            return this.translateService.instant('workflow.board.panel.rule', {
                name : name});
        }
        return this.translateService.instant('workflow.board.panel.action', {
                name : name});
    }

    private initializeCanvasFromScenario(scenario: ScenarioDTO): boolean {
        this.closeEditPanel();

        const steps = (scenario.steps ?? []) as AbstractStepDTO[];
        if (steps.length === 0) {
            this.canvasWorkflow = null;
            this.startNodeId = '';
            this.connections = [];
            this.connectedDropIds = [];
            this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('workflow.board.messages.errorSummary'),
                detail: this.translateService.instant('workflow.board.messages.missingSteps')
            });
            return false;
        }

        if (
            scenario.type === ScenarioDTO.TypeEnum.MARKETING
            && steps[0].type !== AbstractStepDTO.TypeEnum.SAVEQUERY
        ) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translateService.instant('workflow.board.messages.warnSummary'),
                detail: this.translateService.instant('workflow.board.messages.invalidMarketingStart')
            });
        }

        const canvasWorkflow = this.workflowService.createCanvasWorkflow(steps);
        const startNode = canvasWorkflow?.rootBranch?.nodes?.[0];
        if (!canvasWorkflow || !startNode) {
            this.canvasWorkflow = null;
            this.startNodeId = '';
            return false;
        }

        this.canvasWorkflow = canvasWorkflow;
        this.startNodeId = startNode.isStartNode ? startNode.id : '';
        this.nodeHeights.clear();
        this.updateWorkflow();
        return true;
    }

    private resetStepConfigInjector(): void {
        this._stepConfigInjector = null;
        this._lastSelectedNodeId = null;
    }
}
